/**
 * Opt-in A/B toggle for the on-screen keyboard behaviour (diagnostic aid).
 *
 * `?kbmode=content` stores a localStorage flag and adds
 * `interactive-widget=resizes-content` to the viewport meta tag, so the
 * keyboard shrinks the layout viewport (and `window.innerHeight`) instead of
 * only the visual viewport. `?kbmode=default` clears the flag.
 *
 * Dormant by default: with no flag and no query param this module touches
 * neither storage nor the DOM.
 *
 * public/kbmode-init.js does the same job from index.html before the bundle
 * loads (the earliest point available). This module repeats it from
 * main.tsx as a fallback and is the testable reference implementation, so
 * keep the two in sync.
 */

export const KB_MODE_STORAGE_KEY = 'andi-kbmode';
export const KB_MODE_PARAM = 'kbmode';
export const RESIZES_CONTENT_TOKEN = 'interactive-widget=resizes-content';

export type KeyboardMode = 'default' | 'content';

/**
 * Returns viewport meta content with the interactive-widget token added
 * (enabled) or removed (disabled). Every other token is kept as is.
 */
export function withInteractiveWidget(content: string, enabled: boolean): string {
  const tokens = content
    .split(',')
    .map((token) => token.trim())
    .filter((token) => token !== '' && !token.startsWith('interactive-widget'));
  if (enabled) tokens.push(RESIZES_CONTENT_TOKEN);
  return tokens.join(', ');
}

export function getStoredKeyboardMode(): KeyboardMode {
  try {
    return window.localStorage.getItem(KB_MODE_STORAGE_KEY) === 'content' ? 'content' : 'default';
  } catch {
    return 'default';
  }
}

/** The mode the viewport meta tag currently asks for. */
export function getActiveKeyboardMode(): KeyboardMode {
  const meta = document.querySelector('meta[name="viewport"]');
  const content = meta?.getAttribute('content') ?? '';
  return content.includes(RESIZES_CONTENT_TOKEN) ? 'content' : 'default';
}

/**
 * Applies the `kbmode` query param (persisting or clearing the flag), then
 * makes the viewport meta match the stored flag. Returns the resulting mode.
 */
export function applyKeyboardMode(search: string = window.location.search): KeyboardMode {
  let param: string | null = null;
  try {
    param = new URLSearchParams(search).get(KB_MODE_PARAM);
  } catch {
    param = null;
  }
  try {
    if (param === 'content') {
      window.localStorage.setItem(KB_MODE_STORAGE_KEY, 'content');
    } else if (param === 'default') {
      window.localStorage.removeItem(KB_MODE_STORAGE_KEY);
    }
  } catch {
    // Storage unavailable: the toggle then lasts for this page load only.
    if (param === 'content') {
      setViewportMeta(true);
      return 'content';
    }
  }

  const mode = getStoredKeyboardMode();
  if (mode === 'content' || param === 'default') {
    setViewportMeta(mode === 'content');
  }
  return mode;
}

function setViewportMeta(enabled: boolean): void {
  const meta = document.querySelector('meta[name="viewport"]');
  if (!meta) return;
  const current = meta.getAttribute('content') ?? '';
  const next = withInteractiveWidget(current, enabled);
  if (next !== current) meta.setAttribute('content', next);
}
